import ContractSelector from "components/ContractSelector";
import SectionTitle from "components/UI/SectionTitle";
import { PageSection } from "components/UI/Structure/PageSection";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Integrations, { ExportFpiButton } from "./Integrations";
import { ContractRecord, store, ClientRecord } from "lib/DataModel";
import Stack from "@mui/material/Stack";
import { getIn, useFormikContext } from "formik";
import SendItIcon from "components/UI/icons/SendItIcon";
import { useCurrentUser } from "lib/CurrentUserProvider";
import FormWrapper from "components/Formik/FormWrapper";
import IconButton from "@mui/material/IconButton";
import StandardDialog from "components/UI/Dialog/StandardDialog";
import Typography from "@mui/material/Typography";
import FreeTextMultiSelect from "components/UI/FreeTextMultiSelect/FreeTextMultiSelect";
import { omit } from "lodash-es";
import Box from "@mui/material/Box";
import COLORS from "lib/colors";
import * as yup from 'yup';
import { splitTrimFilter } from "lib/utils";
import { FormTextField } from "components/Formik/forms";
import DropdownCheckbox from "components/UI/DropdownCheckbox/DropdownCheckbox";

import { useMutation } from "@apollo/client";
import { gql } from "generated/gql"
import { useNotifications } from "lib/NotificationsProvider";
import Tooltip from "@mui/material/Tooltip";
import Suspenseful from "components/Suspenseful";
import { useOrbit } from "providers/OrbitProvider";
import ContractResource from 'resources/ContractResource'
import FieldsResource from 'resources/FieldsResource'
import ClientCampaignsResource from 'resources/ClientCampaignsResource'
import DegreeProgramsResource from 'resources/DegreeProgramsResource'
import ClientExclusionRulesResource from 'resources/ClientExclusionRulesResource'
import ExportRegistryProvider from "./ExportRegistryProvider";


const FPIUpdateSchema = yup.object().shape({
  toVendorContractIds: yup.array()
    .required('To: Vendor(s) is required')
    .min(1, 'There must be at least one To: Vendor(s) selection'),
  ccEmails: yup.array()
    .of(
      yup.string()
        .email('Each CC: Email must be a valid email address')
        .required('Email is required')
    )
})

interface FPIEmailData {
  toVendorContractIds: string[]
  ccEmails: string[]
  comment?: string
}

interface FPIEmailPreviewProps {
  comment?: string
  clientName: string
}

function FPIEmailPreview({comment, clientName}: FPIEmailPreviewProps) {

  return(
    <Box
      sx={{
        mt: -1,
        p: '12px 18px 18px 18px',
        borderRadius: '5px',
        border: `solid 1px ${COLORS.cadetGray}`,
        minHeight: 160,
        '& > p': {m: 0, mb: 1}
      }}
    >
      <p>Hello,</p>
      <p>Please be advised that your Form Posting Instructions have been <em>updated</em>. <a href="#" onClick={(e) => e.preventDefault()}>Click here to view your Form Posting Instructions.</a></p>
      {comment &&
        <p>{comment}</p>
      }
      <p>Thank you!</p>
      <p>{clientName}</p>
    </Box>
  )
}

interface ContractDetail {
  id: string
  vendorName: string
}

interface SendFPIDialogProps {
  onClose: () => void,
  clientName: string
  contractCollection: ContractDetail[]
}

export function SendFPIDialog({onClose, clientName, contractCollection}: SendFPIDialogProps) {
  const { values, setFieldValue, errors, setErrors, submitForm, isSubmitting } = useFormikContext<FPIEmailData>()


  return(
    <StandardDialog
      title='Send Form Posting Instructions'
      description='Send the current form posting instructions to the selected vendor(s). All vendor-specific values are sent on a per-vendor basis.'
      onCancel={onClose}
      maxWidth='840px'
      onConfirm={() => submitForm()}
      confirmText='Send'
      loading={isSubmitting}
    >
      <Stack direction='column' gap={2} >
        <Typography variant='h4'>Recipient(s)</Typography>
        <DropdownCheckbox
          label="To: Vendor(s)"
          setSelected={(newValue) => setFieldValue('toVendorContractIds', newValue)}
          collection={contractCollection.map(c => c.id)}
          selected={values.toVendorContractIds}
          renderItem={(id) => contractCollection.find(c => c.id === id)?.vendorName || ''}
          error={!!getIn(errors, 'toVendorContractIds')}
          onFocus={() => setErrors(omit(errors, ['toVendorContractIds']))}
          required={true}
        />
        <FreeTextMultiSelect
          label="CC: Email(s)"
          value={values.ccEmails}
          setValue={(newValue) => setFieldValue('ccEmails', newValue)}
          error={!!getIn(errors, 'ccEmails')}
          onFocus={() => setErrors(omit(errors, ['ccEmails']))}
          sx={{ flex: 2 }}
        />

        <>
          <Typography variant='h4'>Comment</Typography>
          <FormTextField
            name='comment'
            label='Comment'
            defaultValue=''
          />
        </>

        <Typography variant='h4'>Email Preview</Typography>
        <FPIEmailPreview comment={values.comment} clientName={clientName} />
      </Stack>
    </StandardDialog>
  )
}

interface SendFPIContainerProps {
  contract: ContractRecord,
  contractCollection: ContractDetail[]
  clientName: string
  onClose: () => void,
  onComplete: (values: FPIEmailData, { setSubmitting }: { setSubmitting: boolean; }) => void,
}

function SendFPIContainer({contract, contractCollection, clientName, onClose, onComplete}: SendFPIContainerProps) {
  const { currentUser } = useCurrentUser()
  const { getRemoteId } = useOrbit()
  const currentUserEmail = getIn(currentUser, 'attributes.email')

  const initialContract = useMemo(() => contractCollection.find(item => item.id === getRemoteId(contract)), [contract, contractCollection, getRemoteId]) as ContractDetail
  const initialRecipients: FPIEmailData = {
    toVendorContractIds: initialContract ? [initialContract.id] : [],
    ccEmails: splitTrimFilter(currentUserEmail, ','),
  }

  return(
    <FormWrapper data={initialRecipients} schema={FPIUpdateSchema} onSubmit={(values, { setSubmitting }) => onComplete({toVendorContractIds: values.toVendorContractIds, ccEmails: values.ccEmails, comment: values.comment}, {setSubmitting: setSubmitting})}>
      <SendFPIDialog onClose={onClose} clientName={clientName} contractCollection={contractCollection} />
    </FormWrapper>
  )
}

const SEND_FPI_EMAILS_MUTATION = gql(`
  mutation SendFpiEmails(
    $contractIds: [ID!]!,
    $cc: [String!],
    $comment: String
  ) {
    mutationResult:sendFpiEmails (input: {
      contractIds: $contractIds
      cc: $cc
      comment: $comment
    }) {
      success
    }
  }
`)

function FPIActionButtons({contract}) {
  const [openSendFPI, setOpenSendFPI] = useState(false)

  const { getRemoteId } = useOrbit()
  const { addNotification } = useNotifications()
  const [gqlSend] = useMutation(SEND_FPI_EMAILS_MUTATION)

  const client = contract && store.cache.query(q => q.findRelatedRecord(contract, 'client')) as ClientRecord

  const contractCollection = useMemo(() => {
    const contracts = client && store.cache.query(q => q.findRelatedRecords(client, 'contracts')) as ContractRecord[]

    return contracts && contracts.map(contract => ({
      id: getRemoteId(contract),
      vendorName: getIn(store.cache.query(q => q.findRelatedRecord(contract, 'vendor')), 'attributes.name'),
    })
  ).filter(item => item.id)}, [client, getRemoteId])

  const handleSendFPI = useCallback((values, { setSubmitting }) => {
    setSubmitting(true)

    const mutation = {
      contractIds: values.toVendorContractIds,
      cc: values.ccEmails,
      comment: values.comment
    }

    const toVendors = contractCollection.filter(c => values.toVendorContractIds.includes(c.id))
    const sentTo = toVendors.length === 1 ? ` ${toVendors[0].vendorName}` : toVendors.length <= 3 ? `: ${toVendors.map(v => v.vendorName).join(', ')}` : ' multiple vendors'

    gqlSend({ variables: mutation }).then(() => {
      setOpenSendFPI(false)
      addNotification({variant: 'notice', message: `Form Posting Instructions sent to${sentTo}.`})
    }).catch(_error => {
      addNotification({variant: 'alert', message: 'Error sending Form Posting Instructions.'})
    }).finally(() => {
      setSubmitting(false)
    })
  }, [contractCollection, gqlSend, addNotification])

  const clientName = getIn(client, 'attributes.name')

  return (
    <Box>
      <Stack direction='row' sx={{ '.MuiSvgIcon-root': {color: COLORS.frenchBlue} }}>
        {contract &&
          <>
            <ExportFpiButton clientName={clientName} />
            <Tooltip title='Send Form Posting Instructions'>
              <IconButton onClick={() => setOpenSendFPI(true)}>
                <SendItIcon />
              </IconButton>
            </Tooltip>
          </>
        }
      </Stack>

      {openSendFPI && contract &&
        <SendFPIContainer contract={contract} contractCollection={contractCollection} clientName={clientName} onClose={() => setOpenSendFPI(false)} onComplete={handleSendFPI} />
      }
    </Box>
  )
}

export default function ClientIntegrationPage() {
  const [contract, setContract] = useState<ContractRecord>()
  const { clientId } = useParams()

  const { remote, getRemoteId } = useOrbit()
  const contractResource = useMemo(() => contract && new ContractResource({ remote, id: getRemoteId(contract) }), [remote, contract, getRemoteId])
  const fieldsResource = useMemo(() => contract && new FieldsResource(remote, contract), [remote, contract])
  const clientCampaignsResource = useMemo(() => contract && new ClientCampaignsResource({remote, contractOrClient: contract}), [remote, contract])
  const degreeProgramsResource = useMemo(() => contract && new DegreeProgramsResource({ remote, contract, includeExclusionRules: true }), [remote, contract])
  const clientExclusionRulesResource = useMemo(() => clientId && new ClientExclusionRulesResource({ remote, client: {type: 'client', id: clientId }}), [remote, clientId])

  return (
    <ExportRegistryProvider>
      <PageSection>
        <Stack direction='column' gap={2} >
          <SectionTitle title='Form Posting Instructions' />
          <Stack direction='row' gap={2} sx={{ alignItems: 'center' }}>
            <ContractSelector
              contract={contract}
              setContract={setContract}
              model="vendor"
              clientId={clientId}
              showAll={false}
              filterSpec={contract => getIn(contract, 'attributes.canPostLeads')}
            />
            <FPIActionButtons contract={contract} />
          </Stack>

          {contractResource && (
            <Suspenseful
              component={Integrations}
              contractResource={contractResource}
              clientCampaignsResource={clientCampaignsResource}
              degreeProgramsResource={degreeProgramsResource}
              clientExclusionRulesResource={clientExclusionRulesResource}
              fieldsResource={fieldsResource}
            />
          )}
        </Stack>
      </PageSection>
    </ExportRegistryProvider>
  )
}
